import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import TrafficLightIndicatorOverall from "../../../components/results/traficLight/trafficLightIndicatorOverall";
import TrafficLightIndicatorNutrition from "../../../components/results/traficLight/trafficLightIndicatorNutrition";
import TrafficLightIndicatorWork from "../../../components/results/traficLight/trafficLightIndicatorWork";
import TrafficLightIndicatorMetabolic from "../../../components/results/traficLight/trafficLightIndicatorMetabolic";
import TrafficLightIndicatorActivity from "../../../components/results/traficLight/trafficLightIndicatorActivity";
import TrafficLightIndicatorLifeStyle from "../../../components/results/traficLight/trafficLightIndicatorLifeStyle";
import TrafficLightIndicatorBurnoutExhaustion from "../../../components/results/traficLight/trafficLightIndicatorBurnoutExhaustion";
import TrafficLightIndicatorBurnoutCynicism from "../../../components/results/traficLight/trafficLightIndicatorBurnoutCynicism";
import TrafficLightIndicatorBurnoutEfficacy from "../../../components/results/traficLight/trafficLightIndicatorBurnoutEfficacy";
import TrafficLightIndicatorEstresPhysical from "../../../components/results/traficLight/trafficLightIndicatorEstresPhysical";
import TrafficLightIndicatorBurnout from "../../../components/results/traficLight/trafficLightIndicatorBurnout";

import recommendationsApi from "../../../api/recommendationsApi";
import estressRecomendations from "../../../data/estressRecomendations";
import { createHbPdfBlobUrl } from "../../../utils/createHbPdf";

import "./hraResultPage.css";
import "./estresResultPage.css";
import "./hbResultPage.css";

const pickFirst = (...values) =>
  values.find((v) => v !== undefined && v !== null);

const normalizeHraResults = (raw) => {
  const source = pickFirst(
    raw?.hra,
    raw?.results?.hra,
    raw?.hraResult,
    raw
  );

  return {
    overallEvaluation: source?.overallEvaluation,
    nutritionalState: source?.nutritionalState,
    workHappiness: source?.workHappiness,
    metabolicRisk: source?.metabolicRisk,
    physicalActivityEvaluation: source?.physicalActivityEvaluation,
    lifeStyle: source?.lifeStyle,
    imc: source?.imc,
    metabolicRiskCauses: Array.isArray(source?.metabolicRiskCauses)
      ? source.metabolicRiskCauses.join(", ")
      : source?.metabolicRiskCauses
  };
};

const normalizeBurnoutResults = (raw) => {
  const source = pickFirst(
    raw?.burnout,
    raw?.estresBurnout,
    raw?.stress,
    raw?.results?.burnout,
    raw?.results?.estresBurnout,
    raw?.results?.stress,
    raw?.stressResult,
    raw
  );

  return {
    hasBurnOut: source?.hasBurnOut,
    exhaustionLevel: source?.exhaustionLevel,
    cynicismLevel: source?.cynicismLevel,
    efficacyLevel: source?.efficacyLevel,
    physicalStress: source?.physicalStress
  };
};

const HraSection = ({ results, recommendations, onRecommendationClick }) => {
  const recs = [
    { key: "overallEvaluation", title: "Evaluación General", text: recommendations.overallEvaluation },
    { key: "nutritionalState", title: "Índice de Masa Corporal", text: recommendations.nutritionalState },
    { key: "workHappiness", title: "Felicidad en el trabajo", text: recommendations.workHappiness },
    { key: "metabolicRisk", title: "Riesgo Metabólico", text: recommendations.metabolicRisk },
    { key: "physicalActivityEvaluation", title: "Nivel de sedentarismo", text: recommendations.physicalActivityEvaluation },
    { key: "lifeStyle", title: "Estilo de vida", text: recommendations.lifeStyle }
  ];

  return (
    <section className="hb-section">
      <header className="result-header">
        <h1>Health Risk Assessment</h1>
        <p>Revisa tus indicadores de salud y estilo de vida</p>
      </header>

      <section className="result-summary card">
        <h2>Resumen General</h2>
        <TrafficLightIndicatorOverall category="Evaluación General" riskLevel={results.overallEvaluation} recommendation="" />
        <button className="reco-link" onClick={() => onRecommendationClick("overallEvaluation")}>Ver recomendación</button>
      </section>

      <section className="metrics-grid">
        <div className="card metric-card">
          <h3>Índice de Masa Corporal</h3>
          <TrafficLightIndicatorNutrition category={results.imc} riskLevel={results.nutritionalState} recommendation="" />
          <button className="reco-link" onClick={() => onRecommendationClick("nutritionalState")}>Ver recomendación</button>
        </div>

        <div className="card metric-card">
          <h3>Felicidad en el trabajo</h3>
          <TrafficLightIndicatorWork category="Felicidad en el Trabajo" riskLevel={results.workHappiness} recommendation="" />
          <button className="reco-link" onClick={() => onRecommendationClick("workHappiness")}>Ver recomendación</button>
        </div>

        <div className="card metric-card">
          <h3>Riesgo Metabólico</h3>
          <TrafficLightIndicatorMetabolic category="Metabólico" riskLevel={results.metabolicRisk} recommendation="" />
          <button className="reco-link" onClick={() => onRecommendationClick("metabolicRisk")}>Ver recomendación</button>
        </div>

        <div className="card metric-card">
          <h3>Sedentarismo</h3>
          <TrafficLightIndicatorActivity category="Sedentarismo" riskLevel={results.physicalActivityEvaluation} recommendation="" />
          <button className="reco-link" onClick={() => onRecommendationClick("physicalActivityEvaluation")}>Ver recomendación</button>
        </div>

        <div className="card metric-card">
          <h3>Estilo de vida</h3>
          <TrafficLightIndicatorLifeStyle category="Estilo Vida" riskLevel={results.lifeStyle} recommendation="" />
          <button className="reco-link" onClick={() => onRecommendationClick("lifeStyle")}>Ver recomendación</button>
        </div>
      </section>

      <section className="recommendations-section card">
        <h2>Recomendaciones por área</h2>
        {recs.map(({ key, title, text }) => (
          <details key={key} id={`rec-${key}`} className="reco-collapse">
            <summary>{title}</summary>
            <p>{text}</p>
          </details>
        ))}
      </section>
    </section>
  );
};

const StressSection = ({ results }) => (
  <section className="hb-section">
    <header className="result-header">
      <h1>Cuestionario de Estrés y Burnout</h1>
      <p>Conoce el resumen de tus resultados y detecta posibles áreas de riesgo.</p>
    </header>

    <section className="info-card card">
      <p>
        El estrés agudo se manifiesta con un conjunto de síntomas físicos y emocionales.
        En el cuestionario aplicado, evaluamos la frecuencia e intensidad de estos síntomas
        para determinar el nivel de estrés vivido en el momento.
      </p>
      <div className="divider" />
      <p>
        De tener un mal manejo, este estrés agudo puede evolucionar a síndrome de burnout,
        depresión o ansiedad.
      </p>
    </section>

    <section className="maslach-card card">
      <h2>En la Escala de Maslach, el agotamiento se mide en tres dimensiones:</h2>
      <p>
        <strong>Cansancio Emocional:</strong> sensación de falta de energía y recursos
        emocionales.
      </p>
      <p>
        <strong>Despersonalización o Cinismo:</strong> actitud negativa y deshumanizada.
      </p>
      <p>
        <strong>Eficacia Laboral:</strong> sensación de logro y satisfacción;
        a mayor eficacia, menor riesgo de burnout.
      </p>
    </section>

    <section className="result-summary card">
      <h2>Resumen General</h2>
      <div className="summary-content">
        <div className="indicator-item">
          <TrafficLightIndicatorEstresPhysical
            category="Nivel de estrés físico:"
            riskLevel={results.physicalStress}
            recommendation={estressRecomendations[results.physicalStress]}
          />
        </div>
        <div className="burnout-indicator">
          <TrafficLightIndicatorBurnout category="Indicador de Burnout:" riskLevel={results.hasBurnOut ? 1 : 0} />
        </div>
      </div>
    </section>

    <section className="result-details">
      <h2>Detalle de Dimensiones de Burnout</h2>
      <div className="grid-dimensions">
        <div className="dimension-card">
          <TrafficLightIndicatorBurnoutExhaustion category="Agotamiento emocional:" riskLevel={results.exhaustionLevel} />
        </div>
        <div className="dimension-card">
          <TrafficLightIndicatorBurnoutCynicism category="Desconexión laboral:" riskLevel={results.cynicismLevel} />
        </div>
        <div className="dimension-card">
          <TrafficLightIndicatorBurnoutEfficacy category="Eficacia profesional:" riskLevel={results.efficacyLevel} />
        </div>
      </div>
    </section>

    <section className="comparison-section">
      <h2>Comparación: Estrés Laboral vs. Burnout</h2>
      <div className="comparative-cards">
        <div className="card compare-card">
          <h3>Estrés Laboral</h3>
          <ul>
            <li>Muy involucrado</li>
            <li>Emociones sobrerreactivas</li>
            <li>Produce urgencia e hiperactividad</li>
            <li>Irritabilidad</li>
            <li>Daño físico predominante</li>
            <li>Conduce a trastornos de ansiedad</li>
            <li>Baja productividad por descuidos</li>
          </ul>
        </div>
        <div className="card compare-card">
          <h3>Burnout</h3>
          <ul>
            <li>Falta de involucramiento</li>
            <li>Emociones embotadas</li>
            <li>Indefensión y desesperanza</li>
            <li>Agotamiento mental</li>
            <li>Daño emocional predominante</li>
            <li>Desapego y depresión</li>
            <li>Falta de motivación en la vida</li>
          </ul>
        </div>
      </div>
    </section>

    <section className="recomendations-section card">
      <h2>Recomendaciones Generales</h2>
      <ol>
        <li>
          <strong>Pausas Laborales:</strong> tómate 5 min de relajación aproximadamente cada hora.
          Te recomendamos realizar técnicas de relajación (respiraciones conscientes, meditaciones
          guiadas, escuchar música) mezcladas con estiramientos.
        </li>
        <li>
          <strong>Organiza tu agenda y tus pendientes:</strong> explora diferentes técnicas para
          priorizar tus tareas, pues pueden percibirse en el cuerpo como factores estresores.
          Estudia métodos como Pomodoro, GTD (Get Things Done) o la matriz de Eisenhower.
        </li>
        <li>
          <strong>Habilidades blandas:</strong> practica la comunicación asertiva y respetuosa
          sobre tus necesidades y preocupaciones. Mejora la escucha activa de tus compañeros,
          aprende a pedir ayuda y a poner límites cuando excedan tus capacidades.
        </li>
        <li>
          <strong>Estilo de vida saludable:</strong> evita estresores como tabaco, exceso de
          cafeína o alcohol. Mantén una rutina de ejercicio (libera endorfinas y controla el estrés),
          horarios regulares de alimentación y una adecuada rutina de sueño.
        </li>
        <li>
          <strong>Busca ayuda profesional:</strong> si tus síntomas de estrés y burnout persisten
          más de dos meses, o afectan tu desempeño laboral y tu vida personal, considera terapia
          psicológica. Un profesional puede orientarte e, incluso, derivarte a atención psiquiátrica.
        </li>
      </ol>
    </section>
  </section>
);

const HbResultPage = () => {
  const { state } = useLocation();
  const rawResults = state?.response;

  const hraResults = useMemo(
    () => normalizeHraResults(rawResults),
    [rawResults]
  );
  const stressResults = useMemo(
    () => normalizeBurnoutResults(rawResults),
    [rawResults]
  );

  const [pdfLoading, setPdfLoading] = useState(false);
  const [recommendations, setRecommendations] = useState({});
  const [loadingRecs, setLoadingRecs] = useState(true);

  useEffect(() => {
    if (!hraResults) return;

    const fetchAllRecs = async () => {
      const keys = [
        { key: "overallEvaluation", section: "overall", level: hraResults.overallEvaluation },
        { key: "nutritionalState", section: "nutrition", level: hraResults.nutritionalState },
        { key: "workHappiness", section: "work", level: hraResults.workHappiness },
        { key: "metabolicRisk", section: "metabolic", level: hraResults.metabolicRisk },
        { key: "physicalActivityEvaluation", section: "activity", level: hraResults.physicalActivityEvaluation },
        { key: "lifeStyle", section: "lifestyle", level: hraResults.lifeStyle }
      ];

      const temp = {};
      for (const { key, section, level } of keys) {
        try {
          const res = await recommendationsApi.getBySectionAndLevel(section, level);
          let text = res.text;
          if (section === "metabolic" && level > 0 && hraResults.metabolicRiskCauses) {
            text = text.replace("{{FACTORES_DE_RIESGO}}", hraResults.metabolicRiskCauses);
          }
          temp[key] = text;
        } catch (error) {
          temp[key] = "No se pudo cargar la recomendación.";
        }
      }

      setRecommendations(temp);
      setLoadingRecs(false);
    };

    fetchAllRecs();
  }, [hraResults]);

  const handleRecommendationClick = (key) => {
    const detail = document.getElementById(`rec-${key}`);
    if (detail) {
      detail.open = true;
      detail.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleDownloadPdf = () => {
    setPdfLoading(true);

    setTimeout(() => {
      const blobUrl = createHbPdfBlobUrl({
        hraResults,
        stressResults,
        hraRecommendations: recommendations
      });

      const pdfWindow = window.open(blobUrl, "_blank");
      if (pdfWindow) {
        setTimeout(() => setPdfLoading(false), 700);
      } else {
        alert("No se pudo abrir la pestaña de PDF.");
        setPdfLoading(false);
      }
    }, 120);
  };

  if (!rawResults) return <h2>No hay datos disponibles</h2>;
  if (loadingRecs) return <h2>Cargando resultados...</h2>;

  return (
    <div className="hb-result-page">
      <button className="print-button" onClick={handleDownloadPdf} disabled={pdfLoading}>
        {pdfLoading ? "Generando PDF..." : "Descargar PDF de resultados"}
      </button>

      <HraSection results={hraResults} recommendations={recommendations} onRecommendationClick={handleRecommendationClick} />
      <StressSection results={stressResults} />
    </div>
  );
};

export default HbResultPage;
